import React from "react";
import { useApolloClient } from "@apollo/client";
import Cookies from "js-cookie";

import {
  Experiment,
  ExperimentsDocument,
  ExperimentsQueryResult,
} from "@analytics/generated/graphql";

import { fireABEvents } from "../events";

import { createResolvablePromise } from "./utils";

type ExperimentContext = {
  query: (experiments: string[]) => Promise<Experiment[]>;
};

const ExperimentContext = React.createContext(
  null as unknown as ExperimentContext
);

type ExperimentProviderProps = {
  mockExperiments?: Experiment[];
  children: React.ReactNode;
  debounceMs?: number;
};

export const ExperimentProvider = ({
  mockExperiments,
  children,
  debounceMs = 50,
}: ExperimentProviderProps) => {
  const client = useApolloClient();

  const instance = React.useRef({
    queue: [] as string[][],
    resolvablePromise: createResolvablePromise<Experiment[]>(),
    timerId: null as NodeJS.Timeout | null,
  });

  async function fire() {
    const { queue, resolvablePromise } = instance.current;

    try {
      instance.current.timerId = null;

      const uniqueExperiments = Array.from(new Set(queue.flat()));
      instance.current.queue = [];
      instance.current.resolvablePromise =
        createResolvablePromise<Experiment[]>();

      const sendRequest = async () => {
        const res = (await client.query({
          query: ExperimentsDocument,
          variables: {
            input: { experiments: uniqueExperiments },
          },
          fetchPolicy: "network-only",
        })) as ExperimentsQueryResult;

        if (!res.data) {
          throw Error("No such experiments");
        }

        const experimentData = res.data.experiments.experiments;

        // Set cookies for each experiment received
        experimentData
          .filter((exp) => !!exp.id)
          .forEach((exp) => {
            if (exp.active) {
              Cookies.set(`catch-AB-${exp.id}`, `${exp.bucket}`, {
                expires: 60,
              });
            } else {
              Cookies.remove(`catch-AB-${exp.id}`);
            }
          });

        return experimentData;
      };

      const experimentData = await sendRequest();

      /**
       * Send another request after things have settled to ensure the ccx cookie is synced correctly as the ccx cookie is set by multiple apis.
       * This will not change anything from the users perspective as the initial buckets were already saved in the catch-AB cookie, even if the ccx cookie was overwritten by another api.
       */
      setTimeout(sendRequest, 2000);

      // Fire AB Testing analytics events
      fireABEvents(experimentData);

      resolvablePromise.resolve(experimentData);
    } catch (e) {
      resolvablePromise.reject(e);
    }
  }

  function scheduleFire() {
    const { timerId } = instance.current;

    if (timerId) {
      clearTimeout(timerId);
      instance.current.timerId = null;
    }

    instance.current.timerId = setTimeout(fire, debounceMs);
  }

  async function query(experiments) {
    function resolveExperiments(resultExperiments) {
      return experiments.map(
        (exp) => resultExperiments.find((res) => res.experiment === exp) ?? null
      );
    }

    if (mockExperiments) {
      return resolveExperiments(mockExperiments);
    }

    const { resolvablePromise } = instance.current;

    instance.current.queue.push(experiments);

    scheduleFire();

    return resolvablePromise.promise.then((results) =>
      resolveExperiments(results)
    );
  }

  return (
    <ExperimentContext.Provider value={{ query }}>
      {children}
    </ExperimentContext.Provider>
  );
};

export const useExperimentContext = (): ExperimentContext => {
  return React.useContext(ExperimentContext);
};
